import { useMutation } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import type { AxiosError } from 'axios';
import { toaster } from '@/components/ui/toaster';
import api from '@/libs/axiosInstance';
import type { Payout } from '@/types/payout';
import type { ApiErrorResponse } from '@fluenceur/types';

interface UpdatePayoutSchedulePayload {
  payoutId: string;
  scheduledPayoutDate: string | null;
}

interface UpdatePayoutScheduleResponse {
  payout: Payout;
}

const updatePayoutSchedule = async (
  payload: UpdatePayoutSchedulePayload
): Promise<UpdatePayoutScheduleResponse> => {
  const { data } = await api.put<Payout>(
    `/dashboard/payouts/${payload.payoutId}/schedule`,
    payload
  );

  return { payout: data };
};

export const useUpdatePayoutScheduleAction = () => {
  const { t } = useTranslation();

  return useMutation({
    mutationFn: (payload: UpdatePayoutSchedulePayload) => updatePayoutSchedule(payload),
    onSuccess: () => {
      toaster.create({
        title: t('payouts.details.scheduleUpdated', 'Payout schedule updated'),
        type: 'success',
      });
    },
    onError: (error: AxiosError<ApiErrorResponse>) => {
      const apiError = error.response?.data;
      const errorCode = apiError?.code;
      const errorMessage = errorCode
        ? t(`errors.${errorCode}`, { defaultValue: apiError?.message || error.message })
        : apiError?.message || error.message || t('common.error');

      toaster.create({
        title: t('payouts.details.errors.scheduleUpdateFailed', 'Failed to update payout schedule'),
        description: errorMessage,
        type: 'error',
      });
    },
  });
};
